import React, { useState, useEffect } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  LocationOn,
  Business,
  AccountTree,
  Warning,
  CheckCircle,
  Schedule,
  Refresh,
  Visibility,
  Group,
  School,
  Assessment,
  Download,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import rootAxios from 'requests/rootAxios';
import trainingTypes from 'Constants/trainingTypes';
import {
  fetchRegions,
  fetchRegionsBranches,
  fetchDailyEmployeesWhoJoined,
} from 'thunks/trainingThunks/thunk';

const TRAINING_TYPE_CONFIG = [
  {
    key: 'REGULAR_INDUCTION',
    label: 'Induction Training',
    value: trainingTypes.REGULAR_INDUCTION,
    bg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    border: '#93c5fd',
    textColor: '#1e40af',
    iconBg: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    circleBg: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  },
  {
    key: 'COLLECTION_TRAINING',
    label: 'Collection Training',
    value: trainingTypes.COLLECTION_TRAINING,
    bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    border: '#fbbf24',
    textColor: '#92400e',
    iconBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    circleBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  },
  {
    key: 'CORRECTIVE_TRAINING',
    label: 'Corrective Training',
    value: trainingTypes.CORRECTIVE_TRAINING,
    bg: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
    border: '#f87171',
    textColor: '#991b1b',
    iconBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    circleBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  },
  {
    key: 'INDIVIDUAL_LENDING',
    label: 'Individual Lending',
    value: trainingTypes.INDIVIDUAL_LENDING,
    bg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
    border: '#4ade80',
    textColor: '#166534',
    iconBg: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    circleBg: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
  },
  {
    key: 'TRAILBLAZE_2_0',
    label: 'Trailblaze 2.0',
    value: trainingTypes.TRAILBLAZE_2_0,
    bg: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
    border: '#a78bfa',
    textColor: '#5b21b6',
    iconBg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    circleBg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  },
  {
    key: 'PRODUCT_FINANCING',
    label: 'Product Financing',
    value: trainingTypes.PRODUCT_FINANCING,
    bg: 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)',
    border: '#22d3ee',
    textColor: '#164e63',
    iconBg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    circleBg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
  },
  {
    key: 'PRAGATI_TRAINING',
    label: 'Pragati Training',
    value: trainingTypes.PRAGATI_TRAINING,
    bg: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
    border: '#f472b6',
    textColor: '#9d174d',
    iconBg: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
    circleBg: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
  },
];

const Dashboard = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [pendencyData, setPendencyData] = useState({
    totalRegions: 0,
    totalCircles: 0,
    totalBranches: 0,
    pendingRegions: 0,
    pendingCircles: 0,
    pendingBranches: 0,
  });
  const [unassignedRegions, setUnassignedRegions] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [pendencyEmployees, setPendencyEmployees] = useState(
    Object.fromEntries(TRAINING_TYPE_CONFIG.map((t) => [t.key, []]))
  );
  const [showUnassignedModal, setShowUnassignedModal] = useState(false);

  const fetchDashboardData = async () => {
    setLoading(true);

    try {
      // Fetch regions
      const regionsResponse = await rootAxios.get('/user/getRegion');
      if (regionsResponse?.data?.data?.regions) {
        const regions = regionsResponse.data.data.regions;
        setAvailableRegions(regions);

        // Fetch branches for all regions
        const regionIds = regions.map((region) => region.regionId);
        const branchesResponse = await rootAxios.post('/user/getRegionBatches', {
          regions: regionIds,
        });

        if (branchesResponse?.data?.data) {
          const branches = branchesResponse.data.data;
          setAvailableBranches(branches);

          // Fetch pendency data for both training types
          await fetchPendencyData(regions, branches);

          // Calculate pendency counts
          const totalRegions = regions.length;
          const totalBranches = branches.length;

          // Calculate pending counts from actual data
          const inductionPending = pendencyEmployees.REGULAR_INDUCTION.length;
          const collectionPending = pendencyEmployees.COLLECTION_TRAINING.length;
          const totalPending = inductionPending + collectionPending;

          setPendencyData({
            totalRegions,
            totalCircles: 0, // This might need a separate API call
            totalBranches,
            pendingRegions: Math.ceil(totalPending / 10), // Estimate based on pending employees
            pendingCircles: Math.ceil(totalPending / 5), // Estimate
            pendingBranches: Math.ceil(totalPending / 2), // Estimate
          });
        }
      }

      // Fetch unassigned regions
      await fetchUnassignedRegions();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch real data from APIs
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleDownloadReport = () => {
    const filterDataForReport = (data) => {
      if (!Array.isArray(data)) {
        return data;
      }

      return data.map((item) => {
        const newItem = { ...item };
        for (const key in newItem) {
          if (
            key.endsWith('_at') ||
            key.endsWith('At') ||
            key.endsWith('_by') ||
            key.endsWith('By') ||
            key === '_id'
          ) {
            delete newItem[key];
          }
        }
        return newItem;
      });
    };

    const wb = XLSX.utils.book_new();

    // "Regions" sheet
    const regionsSheet = XLSX.utils.json_to_sheet(filterDataForReport(availableRegions));
    XLSX.utils.book_append_sheet(wb, regionsSheet, 'Regions');

    // "Branches" sheet
    const branchesSheet = XLSX.utils.json_to_sheet(filterDataForReport(availableBranches));
    XLSX.utils.book_append_sheet(wb, branchesSheet, 'Branches');

    // One sheet per training type
    TRAINING_TYPE_CONFIG.forEach((type) => {
      const sheetName = `Pending ${type.label}`.substring(0, 31); // Excel sheet name limit
      const sheet = XLSX.utils.json_to_sheet(
        filterDataForReport(pendencyEmployees[type.key] ?? [])
      );
      XLSX.utils.book_append_sheet(wb, sheet, sheetName);
    });

    // "Unassigned Regions" sheet
    const unassignedRegionsSheet = XLSX.utils.json_to_sheet(filterDataForReport(unassignedRegions));
    XLSX.utils.book_append_sheet(wb, unassignedRegionsSheet, 'Unassigned Regions');

    const today = new Date();
    const date = today.toISOString();
    XLSX.writeFile(wb, `Dashboard_Report_${date}.xlsx`);
  };

  const fetchPendencyData = async (regions, branches) => {
    try {
      const pendencyDataTemp = {};

      for (const tab of TRAINING_TYPE_CONFIG) {
        const payload = {
          trainingType: tab.value,
          region: regions.map((region) => ({ id: region.regionId, label: region.regionName })),
          branches: branches.map((branch) => ({ id: branch.BRANCH_ID, label: branch.BRANCH_NAME })),
        };

        try {
          const response = await dispatch(fetchDailyEmployeesWhoJoined(payload)).unwrap();

          if (response?.employees) {
            pendencyDataTemp[tab.key] = response.employees;
          } else {
            pendencyDataTemp[tab.key] = [];
          }
        } catch (tabError) {
          console.error(`Error fetching pendency data for ${tab.label}:`, tabError);
          pendencyDataTemp[tab.key] = [];
        }
      }

      setPendencyEmployees(pendencyDataTemp);
    } catch (error) {
      console.error('Error fetching pendency data:', error);
      toast.error('Failed to fetch pendency data');
    }
  };

  const fetchUnassignedRegions = async () => {
    try {
      const response = await rootAxios.post('/user/get-regionsNotAssigned');

      if (response?.data?.statusCode === 200 && response?.data?.data?.data) {
        setUnassignedRegions(response.data.data.data);
      } else {
        setUnassignedRegions([]);
      }
    } catch (error) {
      console.error('Error fetching unassigned regions:', error);
      toast.error('Failed to fetch unassigned regions');
      setUnassignedRegions([]);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color, trend, onClick }) => (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick
          ? {
              transform: 'translateY(-4px)',
              boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }
          : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                background:
                  color === 'primary'
                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                    : color === 'success'
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : color === 'warning'
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : color === 'error'
                          ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                          : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                mr: 2,
                boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.1)',
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="body2" color="#64748b" fontWeight="500" sx={{ mb: 0.5 }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="#94a3b8">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {trend && (
            <Chip
              icon={trend > 0 ? <TrendingUp /> : <TrendingDown />}
              label={`${trend > 0 ? '+' : ''}${trend}%`}
              sx={{
                backgroundColor: trend > 0 ? '#dcfce7' : '#fef2f2',
                color: trend > 0 ? '#166534' : '#991b1b',
                fontWeight: '600',
                fontSize: '0.75rem',
              }}
              size="small"
            />
          )}
        </Box>
        <Typography
          variant="h2"
          component="div"
          fontWeight="700"
          sx={{
            color: '#1e293b',
            mb: 1,
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          {value}
        </Typography>
        {onClick && (
          <Box display="flex" alignItems="center" color="#3b82f6" sx={{ mt: 2 }}>
            <Visibility sx={{ fontSize: '1rem', mr: 0.5 }} />
            <Typography variant="caption" fontWeight="500">
              View Details
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Professional Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{
                  color: '#1e293b',
                  mb: 0.25,
                }}
              >
                Training Management Dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                Comprehensive overview of training operations and regional management
              </Typography>
            </Box>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Button
                variant="contained"
                color="success"
                startIcon={<Download />}
                onClick={handleDownloadReport}
                sx={{
                  borderRadius: '12px',
                  px: 3,
                  py: 1.5,
                  fontWeight: '600',
                  textTransform: 'none',
                }}
              >
                Download All Reports
              </Button>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={() => {
                  setLoading(true);
                  fetchDashboardData();
                }}
                disabled={loading}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '12px',
                  px: 3,
                  py: 1.5,
                  fontWeight: '600',

                  textTransform: 'none',
                  boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px 0 rgba(59, 130, 246, 0.4)',
                  },
                }}
              >
                Refresh Data
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Key Metrics Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Regions"
              value={pendencyData.totalRegions}
              subtitle="Active regions"
              icon={<LocationOn />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Branches"
              value={pendencyData.totalBranches}
              subtitle="Branch locations"
              icon={<Business />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Training"
              value={Object.values(pendencyEmployees).reduce((sum, arr) => sum + arr.length, 0)}
              subtitle="Employees awaiting training"
              icon={<School />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Unassigned Regions"
              value={unassignedRegions.length}
              subtitle="Need trainer assignment"
              icon={<Warning />}
              color="error"
              onClick={() => setShowUnassignedModal(true)}
            />
          </Grid>
        </Grid>

        {/* Training Breakdown Section */}
        <Card
          sx={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" mb={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white',
                  mr: 2,
                  boxShadow: '0 4px 14px 0 rgba(139, 92, 246, 0.3)',
                }}
              >
                <Assessment />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b', mb: 0.5 }}>
                  Training Breakdown
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Pending employees by training category
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {TRAINING_TYPE_CONFIG.map((type) => (
                <Grid item xs={12} md={6} lg={4} key={type.key}>
                  <Box
                    sx={{
                      p: 4,
                      background: type.bg,
                      borderRadius: '16px',
                      border: `1px solid ${type.border}`,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 80,
                        height: 80,
                        background: type.circleBg,
                        borderRadius: '50%',
                        opacity: 0.1,
                      }}
                    />
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '8px',
                          background: type.iconBg,
                          color: 'white',
                          mr: 2,
                        }}
                      >
                        <School />
                      </Box>
                      <Typography variant="h6" fontWeight="600" sx={{ color: type.textColor }}>
                        {type.label}
                      </Typography>
                    </Box>
                    <Typography variant="h2" fontWeight="800" sx={{ color: type.textColor, mb: 1 }}>
                      {pendencyEmployees[type.key]?.length ?? 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: type.textColor, opacity: 0.8 }}>
                      Employees pending
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Unassigned Regions Modal */}
        <Dialog
          open={showUnassignedModal}
          onClose={() => setShowUnassignedModal(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 4,
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #fef2f2 0%, #fef7f7 100%)',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    mr: 2,
                    boxShadow: '0 4px 14px 0 rgba(239, 68, 68, 0.3)',
                  }}
                >
                  <Warning />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b' }}>
                    Unassigned Regions
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {unassignedRegions.length} regions need trainer assignment
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ color: '#64748b', mb: 4, lineHeight: 1.6 }}>
                The following regions do not have trainers assigned. Review the details below and
                take necessary action to assign trainers.
              </Typography>

              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Table>
                  <TableHead
                    sx={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}
                  >
                    <TableRow>
                      <TableCell sx={{ fontWeight: '700', color: '#1e293b' }}>Region ID</TableCell>
                      <TableCell sx={{ fontWeight: '700', color: '#1e293b' }}>
                        Region Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: '700', color: '#1e293b' }}>RM Name</TableCell>
                      <TableCell sx={{ fontWeight: '700', color: '#1e293b' }}>
                        Total Circles
                      </TableCell>
                      <TableCell sx={{ fontWeight: '700', color: '#1e293b' }}>
                        Total Trainers
                      </TableCell>
                      <TableCell sx={{ fontWeight: '700', color: '#1e293b' }}>Circles</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unassignedRegions.map((region) => (
                      <TableRow
                        key={region.regionId}
                        sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}
                      >
                        <TableCell sx={{ fontWeight: '600', color: '#475569' }}>
                          {region.regionId}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="subtitle2"
                            fontWeight="700"
                            sx={{ color: '#1e293b' }}
                          >
                            {region.regionName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={region.rmName || 'N/A'}
                            sx={{
                              backgroundColor:
                                region.rmName && region.rmName !== 'N/A' ? '#dcfce7' : '#f1f5f9',
                              color:
                                region.rmName && region.rmName !== 'N/A' ? '#166534' : '#64748b',
                              fontWeight: '600',
                              border: '1px solid',
                              borderColor:
                                region.rmName && region.rmName !== 'N/A' ? '#bbf7d0' : '#e2e8f0',
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#475569' }}>
                          {region.totalCircles}
                        </TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#475569' }}>
                          {region.totalTrainers}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ maxWidth: 200 }}>
                            {region.circles && region.circles.length > 0 ? (
                              region.circles.map((circle, index) => (
                                <Chip
                                  key={circle.circleId}
                                  label={circle.circleName}
                                  size="small"
                                  sx={{
                                    m: 0.5,
                                    backgroundColor: '#dbeafe',
                                    color: '#1e40af',
                                    border: '1px solid #93c5fd',
                                    fontWeight: '500',
                                  }}
                                />
                              ))
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{ color: '#94a3b8', fontStyle: 'italic' }}
                              >
                                No circles assigned
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {unassignedRegions.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                      display: 'inline-flex',
                      mb: 3,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 64, color: '#16a34a' }} />
                  </Box>
                  <Typography variant="h5" fontWeight="700" sx={{ color: '#16a34a', mb: 2 }}>
                    All Regions Assigned
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#64748b', maxWidth: 400, mx: 'auto' }}>
                    Excellent! All regions have trainers assigned. Your training network is fully
                    operational.
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 4, borderTop: '1px solid #e2e8f0' }}>
            <Button
              onClick={() => setShowUnassignedModal(false)}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                fontWeight: '600',
                textTransform: 'none',
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowUnassignedModal(false);
                fetchUnassignedRegions();
              }}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                fontWeight: '600',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                },
              }}
            >
              Refresh Data
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default Dashboard;
